import { useParams, useNavigate } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { money } from "../../lib/api";
import { Loading } from "../../components/States";
import { StageBar, StatusPill } from "../../components/StageBar";
import { ArrowLeft, MapPin, Wrench, CalendarCheck, FileSignature } from "lucide-react";

export default function LotDetail() {
  const { id } = useParams();
  const { data, loading } = useFetch(`/lots/${id}`);
  const navigate = useNavigate();

  if (loading || !data) return <Loading />;
  const { lot, jobs, inspections, change_orders } = data;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="hw-card hw-glow p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="hw-eyebrow mb-1">{lot.label}</div>
            <h1 className="font-head text-4xl font-extrabold text-white">{lot.address}</h1>
            <div className="mt-2 flex items-center gap-2 text-slate-400"><MapPin size={14} /> Contract value {money(lot.contract_price)}</div>
          </div>
          <StatusPill status={lot.inspection_status} />
        </div>
        <div className="mt-6"><StageBar stage={lot.stage} /></div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Electrical Phases / Jobs" icon={Wrench}>
          {jobs.map((j) => (
            <Row key={j.id} title={j.title} sub={`${j.phase} · ${j.assigned_name}`} pill={j.status} />
          ))}
          {jobs.length === 0 && <Muted>No jobs on this lot yet.</Muted>}
        </Section>

        <Section title="Inspections" icon={CalendarCheck}>
          {inspections.map((i) => (
            <Row key={i.id} title={`${i.type} — ${i.inspector}`} sub={i.failure_reason || i.jurisdiction} pill={i.result} />
          ))}
          {inspections.length === 0 && <Muted>No inspections scheduled.</Muted>}
        </Section>

        <Section title="Change Orders" icon={FileSignature}>
          {change_orders.map((c) => (
            <Row key={c.id} title={`${c.number} · ${money(c.amount)}`} sub={c.description} pill={c.status} />
          ))}
          {change_orders.length === 0 && <Muted>No change orders.</Muted>}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="hw-card p-6">
      <h3 className="mb-4 flex items-center gap-2 font-head text-xl font-bold text-white"><Icon size={18} className="text-cyan-400" /> {title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ title, sub, pill }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/40 px-4 py-3">
      <div className="min-w-0">
        <div className="truncate font-medium text-white">{title}</div>
        {sub && <div className="truncate text-sm text-slate-400">{sub}</div>}
      </div>
      <StatusPill status={pill} />
    </div>
  );
}
function Muted({ children }) {
  return <p className="text-sm text-slate-500">{children}</p>;
}
